import AppList, {AppListConfig} from "../../../../../components/layout/list";
import {
    Person,
    PersonDocument,
    PersonDocumentRestControllerApiFactory,
    PersonDocumentExportSummary,
    PersonDocumentType,
    PersonRestControllerApiFactory,
} from "arteco-api-client-ts";
import React, {useEffect, useState} from "react";
import {useApiManager} from "../../../../../components/utils/api";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TableCell,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {dateToString} from "../../../../../components/utils/dates";
import {usePerson} from "../index";
import {fileLocalSave} from "../../../../../components/form/form_inputs/form_file";
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {parsePathId} from "../../../../../components/form/form_utils";
import {types, useType} from "../../../../../components/form/types";
import {btnSx, SoftChip} from "../../../../../components/kit";
import {personName} from "../header";
import {envVars} from "../../../../../components/utils/env_vars";

export const PersonDocumentListPage = () => {
    const params = useParams();
    const apiManager = useApiManager();
    
    const navigate = useNavigate();

    const peopleCtrl = apiManager.factory(PersonRestControllerApiFactory);
    const perDocType = useType(types.personDocumentTypes);


    const personId = parsePathId(params, "personId");
    const [person, setPerson] = usePerson(personId);
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const [exportYear, setExportYear] = useState<string>('');
    const [exportType, setExportType] = useState<string>('');
    const [exportPerson, setExportPerson] = useState<string>('');
    const [docTypes, setDocTypes] = useState<PersonDocumentType[]>([]);
    const [people, setPeople] = useState<Person[]>([]);
    const [includeFormer, setIncludeFormer] = useState<boolean>(true);
    const [namePrefix, setNamePrefix] = useState<boolean>(true);
    const [flat, setFlat] = useState<boolean>(false);
    const [summary, setSummary] = useState<PersonDocumentExportSummary | undefined>();

    // Selección para borrar varios a la vez sin entrar en cada ficha (#14192)
    const [pageRows, setPageRows] = useState<PersonDocument[]>([]);
    const [selected, setSelected] = useState<Map<number, PersonDocument>>(new Map());
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [trigger, setTrigger] = useState(0);

    // Filtro por tipo, en el servidor y en la URL: se conserva al entrar en un documento y volver (#14404)
    const [searchParams, setSearchParams] = useSearchParams();
    const typeFilter = parseInt(searchParams.get("type") || "") || undefined;
    const setTypeFilter = (id: number | undefined) => {
        const next = new URLSearchParams(searchParams);
        if (id) next.set("type", String(id)); else next.delete("type");
        setSearchParams(next, {replace: true});
    };

    const documentCtrl = apiManager.factory(PersonDocumentRestControllerApiFactory);

    const getPersonId = (document: PersonDocument | undefined): number | undefined => {
        if (person) {
            return person.id;
        } else if (document) {
            return document.person.id;
        } else {
            return undefined;
        }
    }

    useEffect(() => {
        apiManager.execute(peopleCtrl.listPerson())
            .then(people => setPeople(people));
        perDocType.supplier()
            .then(types => setDocTypes(types))
    }, []);

    const exportFilters = () => ({
        year: parseInt(exportYear) || undefined,
        type: parseInt(exportType) || undefined,
        person: parseInt(exportPerson) || undefined,
    });

    // Cuántos documentos y cuántos MB va a bajar, antes de pulsar (#14144)
    useEffect(() => {
        if (!modalOpen) {
            return;
        }
        setSummary(undefined);
        const f = exportFilters();
        apiManager.execute(documentCtrl.exportSummaryPersonDocument(f.year, f.type, f.person, includeFormer))
            .then(s => setSummary(s));
    }, [modalOpen, exportYear, exportType, exportPerson, includeFormer]);

    // El ZIP lo descarga el navegador directamente, que lo va guardando en disco: con toda la plantilla
    // son cientos de MB y por el cliente generado (base64 en memoria) no caben (#14144).
    const doExport = () => {
        const f = exportFilters();
        const params = new URLSearchParams();
        if (f.year) params.set("year", String(f.year));
        if (f.type) params.set("type", String(f.type));
        if (f.person) params.set("person", String(f.person));
        params.set("includeFormer", String(includeFormer));
        params.set("namePrefix", String(namePrefix));
        params.set("flat", String(flat));
        const a = document.createElement("a");
        a.href = `${envVars.apiBasePath}/api/admin/employee/person/document/export?${params}`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setModalOpen(false);
    };

    const toggle = (bean: PersonDocument) => {
        if (!bean.id) return;
        const next = new Map(selected);
        if (next.has(bean.id)) next.delete(bean.id); else next.set(bean.id, bean);
        setSelected(next);
    };

    const pageIds = pageRows.filter(r => r.id && !r.dismissed).map(r => r.id!);
    const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

    const togglePage = () => {
        const next = new Map(selected);
        pageRows.filter(r => r.id && !r.dismissed).forEach(r => allPageSelected ? next.delete(r.id!) : next.set(r.id!, r));
        setSelected(next);
    };

    const deleteSelected = () => {
        const n = selected.size;
        setDeleting(true);
        apiManager.execute(documentCtrl.deleteOrDismissInBatchPersonDocument([...selected.keys()]),
            n === 1 ? "1 archivo eliminado" : `${n} archivos eliminados`)
            .then(() => setSelected(new Map()))
            .finally(() => {
                setDeleting(false);
                setConfirmOpen(false);
                setTrigger(t => t + 1);
            });
    };

    const listConfig: AppListConfig<PersonDocument> = {
        title: person ? "Archivos de " + personName(person) : "Archivos de la plantilla",
        onClick: (document: PersonDocument) => {
            const personId = getPersonId(document);
            if (personId) {
                navigate("/app/admin/employee/person/" + personId + "/document/" + document.id);
            } else {
                alert("No personId to forward!");
            }
        },
        headers: [
            {title: "", width: 44},
            {title: "Fecha", width: 130},
            !personId ? {title: "Persona"} : null,
            {title: "Tipo"},
            {title: "Título", width: "38%"},
            {title: "", align: "right", width: 104},
        ],
        decorator: (bean: PersonDocument) =>
            <>
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    {!bean.dismissed &&
                        <Checkbox size="small" checked={!!bean.id && selected.has(bean.id)} onChange={() => toggle(bean)}
                                  inputProps={{"aria-label": "Seleccionar " + bean.title}}/>}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={600}>{dateToString(bean.created)}</Typography>
                </TableCell>
                {!personId &&
                    <TableCell><Typography variant="body2" noWrap>{personName(bean.person)}</Typography></TableCell>
                }
                <TableCell>{bean.type?.name ? <SoftChip tone="neutral" label={bean.type.name}/> : "—"}</TableCell>
                <TableCell>
                    <Typography variant="body2" noWrap title={bean.title}>{bean.title}</Typography>
                    <Stack direction="row" spacing={0.75} sx={{mt: 0.25}} flexWrap="wrap" useFlexGap>
                        {bean.visible && <SoftChip tone="info" label="Visible por el empleado"/>}
                        {bean.expiration && (
                            <Typography variant="caption" color="text.secondary">Caduca el {dateToString(bean.expiration)}</Typography>
                        )}
                    </Stack>
                </TableCell>
                <TableCell align={"right"} sx={{whiteSpace: "nowrap"}}>
                    <Tooltip title={"Descargar con el nombre del fichero"}>
                    <IconButton size={"small"}
                            aria-label={"Descargar con el nombre del fichero"}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (bean.id) {
                                    apiManager.executeRaw(documentCtrl.viewPersonDocument(bean.id))
                                        .then((resp) => fileLocalSave(resp));
                                }
                            }}>
                        <DownloadRoundedIcon fontSize="small"/>
                    </IconButton>
                    </Tooltip>
                    <Tooltip title={"Descargar con el nombre de la persona"}>
                    <IconButton size={"small"}
                            aria-label={"Descargar con el nombre de la persona"}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (bean.id) {
                                    apiManager.executeRaw(documentCtrl.viewPersonDocument(bean.id))
                                        .then((resp) => fileLocalSave(resp, bean.person.surnamesAndName));
                                }
                            }}>
                        <BadgeRoundedIcon fontSize="small"/>
                    </IconButton>
                    </Tooltip>
                </TableCell>
            </>,
        supplier: async (currentPage, size, term, showDismissed) => {
            // Posicionales: typeId, personId, year, month, visibleByEmployee, term, showDismissed.
            const page = documentCtrl.pagePersonDocument(
                currentPage,
                size,
                undefined,
                typeFilter,
                personId,
                undefined,
                undefined,
                undefined,
                term,
                showDismissed
            );
            page.then(resp => setPageRows(resp.data.content || [])).catch(() => setPageRows([]));
            return page;
        },
        toolbar:
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 1.5}}>
                <TextField select size="small" label="Tipo de documento" value={typeFilter ?? ""}
                           SelectProps={{displayEmpty: true}} InputLabelProps={{shrink: true}}
                           onChange={e => setTypeFilter(e.target.value === "" ? undefined : Number(e.target.value))}
                           sx={{width: {xs: "100%", sm: 280}, "& .MuiOutlinedInput-root": {borderRadius: 2}}}>
                    <MenuItem value="">Todos los tipos</MenuItem>
                    {docTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
                <Button size="small" sx={btnSx} onClick={togglePage} disabled={pageIds.length === 0}>
                    {allPageSelected ? "Quitar los de esta página" : "Seleccionar los de esta página"}
                </Button>
                {selected.size > 0 && <>
                    <Typography variant="body2" color="text.secondary">
                        {selected.size === 1 ? "1 seleccionado" : `${selected.size} seleccionados`}
                    </Typography>
                    <Button size="small" sx={btnSx} onClick={() => setSelected(new Map())}>Quitar la selección</Button>
                    <Button size="small" variant="contained" color="error" sx={btnSx}
                            startIcon={<DeleteOutlineRoundedIcon/>} onClick={() => setConfirmOpen(true)}>
                        Borrar
                    </Button>
                </>}
            </Stack>,
        actions: [
            {
                label: "Exportar por lotes",
                icon: <FileDownloadRoundedIcon/>,
                action: () => setModalOpen(true)
            }
        ]
    };


    return (
        <>
            <AppList config={listConfig} trigger={trigger} filterKey={String(typeFilter ?? "")} appendable={person != undefined} onAppend={() => {
                const personId = getPersonId(undefined);
                if (personId) {
                    navigate("/app/admin/employee/person/" + personId + "/document/new");
                } else {
                    alert("No personId to forward!");
                }
            }}/>

            <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} fullWidth maxWidth="xs"
                    PaperProps={{sx: {borderRadius: 3}}}>
                <DialogTitle sx={{fontWeight: 700, pb: 0.5}}>
                    {selected.size === 1 ? "¿Borrar 1 archivo?" : `¿Borrar ${selected.size} archivos?`}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={0.5} sx={{maxHeight: 240, overflowY: "auto", pt: 1}}>
                        {[...selected.values()].map(d => (
                            <Typography key={d.id} variant="body2" noWrap title={d.title}>
                                {!personId && d.person ? personName(d.person) + " · " : ""}{d.title}
                                <Typography component="span" variant="caption" color="text.secondary">
                                    {" · " + dateToString(d.created)}{d.type?.name ? " · " + d.type.name : ""}
                                </Typography>
                            </Typography>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting} sx={btnSx}>Cancelar</Button>
                    <Button variant={"contained"} color="error" disabled={deleting} sx={btnSx}
                            onClick={deleteSelected}>{deleting ? "Borrando…" : "Borrar"}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="xs"
                    PaperProps={{sx: {borderRadius: 3}}}>
                <DialogTitle sx={{fontWeight: 700, pb: 0.5}}>Exportar archivos por lotes</DialogTitle>
                <Typography variant="body2" color="text.secondary" sx={{px: 3}}>
                    Todos los filtros son opcionales: sin ninguno, se exportan todos.
                </Typography>
                <DialogContent>
                    <Box sx={{pt: 1}}>
                        <TextField sx={{mb: 2}} fullWidth label={"Año"} type={"number"} value={exportYear}
                                   helperText={"El del documento; si no tiene, el de la subida"}
                                   onChange={e => setExportYear(e.target.value)}/>
                        <FormControl fullWidth sx={{mb: 2}}>
                            <InputLabel id="docTypeLabelId">Tipo de documento</InputLabel>
                            <Select
                                fullWidth
                                value={exportType}
                                label={"Tipo de documento"}
                                labelId={"docTypeLabelId"}
                                onChange={e => setExportType(e.target.value)}>
                                <MenuItem value={''}>Todos</MenuItem>
                                {docTypes && docTypes.map(docType => (
                                    <MenuItem key={docType.id} value={docType.id}>{docType.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{mb: 1}}>
                            <InputLabel id="personLabelId">Persona</InputLabel>
                            <Select
                                fullWidth
                                value={exportPerson}
                                label="Persona"
                                labelId="personLabelId"
                                onChange={e => setExportPerson(e.target.value)}>
                                <MenuItem value={''}>Todas</MenuItem>
                                {people && people.map(person => (
                                    <MenuItem key={person.id} value={person.id}>{person.surnamesAndName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack>
                            <FormControlLabel label="Incluir al personal de baja" control={
                                <Switch checked={includeFormer} onChange={e => setIncludeFormer(e.target.checked)}/>
                            }/>
                            <FormControlLabel label="Nombre y apellidos delante de cada fichero" control={
                                <Switch checked={namePrefix} onChange={e => setNamePrefix(e.target.checked)}/>
                            }/>
                            <FormControlLabel label="Todo en una carpeta, sin subcarpetas" control={
                                <Switch checked={flat} onChange={e => setFlat(e.target.checked)}/>
                            }/>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 1}}>
                            {flat
                                ? `Ejemplo: ${namePrefix ? "Pérez Gil, Ana - " : ""}Curriculum vitae - 2025-06-15 - CV.pdf`
                                : `Ejemplo: 2025/Pérez Gil, Ana/Curriculum vitae/${namePrefix ? "Pérez Gil, Ana - " : ""}2025-06-15 - CV.pdf`}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{mt: 2}}>
                            {summary === undefined ? "Calculando…"
                                : summary.documents === 0 ? "Ningún documento con estos filtros"
                                    : `${summary.documents} ${summary.documents === 1 ? "documento" : "documentos"} · ${((summary.bytes ?? 0) / 1048576).toFixed((summary.bytes ?? 0) < 10485760 ? 1 : 0)} MB`}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button onClick={() => setModalOpen(false)} sx={btnSx}>Cancelar</Button>
                    <Button variant={"contained"} disabled={!summary || summary.documents === 0} sx={btnSx}
                            onClick={doExport}>Exportar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};